import React, {useEffect, useState} from 'react'
import {PersonnelPageTitle} from '../../../../_metronic/layout/core/PersonnelPageData'
import {useAuth} from '../../../modules/auth'
import db from '../../../../services/firebase'
import {PageLink} from '../../../../_metronic/layout/core'
import {KTCard} from '../../../../_metronic/helpers'
import {AddPersonnelGroupModal} from '../components/AddPersonnelGroup'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import {Button, Form} from 'react-bootstrap'
import {format, parseISO} from 'date-fns'
import {ToastContainer} from 'react-toastify'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import Searchbar from 'app/components/Searchbar'
import useAuditContext from 'app/Hooks/useAuditContext'

const usersBreadcrumbs: Array<PageLink> = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

interface UserData {
  uid: string
  id: string
  fullName: string
  role: string
  avatarUrl: string
  tenantId: string
  policiesAccepted?: boolean
  groupName: string
  checklist: string
  createdOn: string
  createdBy: String
}

interface Group {
  id: string
  group: string
}
const PersonnelGroup = () => {
  const [users, setUsers] = useState<UserData[]>([])
  const {currentUser} = useAuth()
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState<UserData[]>([])
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [checkedItems, setCheckedItems] = useState<any[]>([])
  const [checklists, setCheckists] = useState<any>([])
  const [groupCounts, setGroupCounts] = useState({} as {[key: string]: number})
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [totalgroup, setTotalgroup] = useState(0)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [folderName, setFolderName] = useState<string>('')
  const [oldGroupName, setOldGroupName] = useState<string>('')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false)
  const { currentAuditContext } = useAuditContext()

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    getPersonnelGroup()
  }, [])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'personnel_checklist')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }))
      setCheckists(risksData)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    setFilteredItems(users)
  }, [users])

  useEffect(() => {
    if (!currentUser?.tenantId) {
      return
    }

    const fetchGroups = async () => {
      const tenantId = currentUser.tenantId
      const tenantRef = doc(db, 'tenants', tenantId!)
      const policyRef = collection(tenantRef, 'employees')

      const unsubscribe = onSnapshot(policyRef, (snapshot) => {
        const groupsData = snapshot.docs
          .map((doc) => ({
            ...(doc.data() as Group),
            id: doc.id,
            role: doc.data().role,
          }))
          .filter((item) => item.role !== 'Auditor')

        const groupCounts = groupsData.reduce((counts, group) => {
          const checklistName = group.group || '[Default]'
          counts[checklistName] = (counts[checklistName] || 0) + 1
          return counts
        }, {})

        setGroupCounts(groupCounts)
      })

      return () => unsubscribe()
    }

    fetchGroups()
  }, [currentUser?.tenantId])

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredItems(users)
      setSearchLength(false)
    } else {
      const filteredItems = users.filter(
        (user) => user.groupName && user.groupName.toLowerCase().includes(searchTerm)
      )
      setFilteredItems(filteredItems)
      setSearchLength(filteredItems.length === 0)
    }
  }, [searchTerm, users])

  const getPersonnelGroup = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'personnel_group')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs.map((doc) => ({
          ...(doc.data() as UserData),
          id: doc.id,
        }))
        setUsers(risksData)
        setTotalgroup(risksData.length)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting personnels:', error)
    }
  }

  const handleChange = async (userId: string, e: React.ChangeEvent<HTMLSelectElement>) => {
    const newChecklist = e.target.value

    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)

    const assetRef = doc(tenantRef, 'personnel_group', userId)
    await updateDoc(assetRef, {checklist: newChecklist})
  }

  const formatTimestamp = (timestamp) => {
    if (timestamp) {
      let date
      if (typeof timestamp === 'string') {
        if (timestamp.includes('-')) {
          date = parseISO(timestamp)
        } else if (timestamp.includes('/')) {
          const parts = timestamp.split('/')
          const month = +parts[0]
          const day = +parts[1]
          const year = +parts[2]
          date = new Date(year, month - 1, day)
        }
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000)
      } else {
        date = new Date(timestamp)
      }

      if (isNaN(date.getTime())) {
        return 'Invalid Timestamp'
      }

      return format(date, 'd MMMM, yyyy')
    }
    return '--'
  }

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const handleDeleteClick = async (item) => {
    const count = groupCounts[item?.groupName] || 0
    if (count > 0) {
      showErrorToast({
        message:
          'This group has more than one employee. Please remove the employees before deleting the group.',
      })
      return
    }
    if (totalgroup <= 1) {
      showErrorToast({message: 'Cannot delete the last group. At least one group must remain.'})
      return
    }

    setSelectedGroup(item)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'personnel_group')
      const docRef = doc(policyRef, selectedGroup.id)
      await deleteDoc(docRef)

      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()

      const todaysDate = month + '/' + day + '/' + year

      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `Group '${selectedGroup?.groupName}' has been deleted by ${currentUser?.userName}'`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        action: 'Group deleted',
        target: 'Employees',
        date: todaysDate,
      })
      showSuccessToast({message: 'Group deleted successfully'})
    } catch (error) {
      showErrorToast({message: 'Something went wrong'})
    } finally {
      setIsDeleting(false)
      setIsDeleteModalOpen(false)
      setDropdownVisible(false)
      setSelectedGroup(null)
    }
  }

  const handleCheckChange = (e) => {
    const {checked, value} = e.target
    setCheckedItems((prev) => (checked ? [...prev, value] : prev.filter((item) => item !== value)))
  }

  const handleSelectAllChange = (e) => {
    setSelectAll(e.target.checked)
    if (e.target.checked) {
      setCheckedItems(filteredItems.map((item) => item.id))
    } else {
      setCheckedItems([])
    }
  }

  const handleDeselectClick = () => {
    setCheckedItems([])
    setSelectAll(false)
  }

  const handleCheckedRemoveClick = () => {
    if (totalgroup <= 1) {
      showErrorToast({message: 'Cannot delete the last group. At least one group must remain.'})
      return
    }
    const hasEmployees = checkedItems.some((id) => {
      const group = users.find((user) => user.id === id)
      return group && groupCounts[group.groupName] > 0
    })

    if (hasEmployees) {
      showErrorToast({
        message:
          'One or more selected groups have employees. Please remove the employees before deleting the groups.',
      })
      return
    }
    setIsBulkDeleteModalOpen(true)
  }

  const handleBulkDeleteConfirm = async () => {
    setIsDeleting(true)
    try {
      for (const id of checkedItems) {
        const tenantId = currentUser?.tenantId || ''
        const tenantRef = doc(db, 'tenants', tenantId)
        const policyRef = collection(tenantRef, 'personnel_group')
        const groupDoc = await getDoc(doc(policyRef, id))

        if (groupDoc.exists() && groupDoc.data().name !== 'Default') {
          const docRef = doc(policyRef, id)
          await deleteDoc(docRef)
        }
      }

      showSuccessToast({message: 'Groups have been removed successfully'})
      setIsBulkDeleteModalOpen(false)
      setCheckedItems([])
      setSelectAll(false)
    } catch (error) {
      console.error('Error deleting groups:', error)
      showErrorToast({message: 'An error occurred while deleting the groups'})
    } finally {
      setIsDeleting(false)
      setDropdownVisible(false)
    }
  }

  const handleRename = async (id: string) => {
    if (!folderName.trim()) {
      return
    }
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const assetRef = doc(tenantRef, 'personnel_group', id)
    await updateDoc(assetRef, {groupName: folderName})

    const employeesRef = collection(tenantRef, 'employees')
    const q = query(employeesRef, where('group', '==', oldGroupName))
    const snapshot = await getDocs(q)
    snapshot.forEach((doc) => {
      const employeeRef = doc.ref
      updateDoc(employeeRef, {group: folderName})
    })
    showSuccessToast({message: 'Group name updated successfully'})
    setFolderName('')
    setEditingId(null)
  }

  const handleEditClick = (id: string, currentName: string) => {
    setEditingId(id)
    setFolderName(currentName)
    setOldGroupName(currentName)
  }

  const handleCancel = () => {
    setFolderName('')
    setEditingId(null)
  }
  return (
    <>
      <PersonnelPageTitle
        icon='Icon.svg'
        title='Employees'
        // desc={totalgroup.toString() + ' Groups'}
        desc='Manage your employees to ensure compliance and simplify audits'
        breadcrumbs={usersBreadcrumbs}
      >
        Personnel Management
      </PersonnelPageTitle>
      <KTCard>
        <div>
          <div className='card-header border-0 pt-6 h-200'>
            <div className='card-title'>
              <div className='flex-lg-fill'>
                <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
              </div>
            </div>
            <div className='card-toolbar'>
              {checkedItems.length > 0 ? (
                <>
                  {checkedItems.length > 0 ? (
                    <div className=' m-1 border-end pe-4 text-center' style={{color: '#A1A5B7'}}>
                      <span>
                        <p className='pt-4'>{checkedItems.length} Selected</p>
                      </span>
                    </div>
                  ) : null}

                  <div className='mx-5' style={{cursor: 'pointer'}} onClick={handleDeselectClick}>
                    <img src='/media/buttons/Deselect.svg' alt='Deselect' />
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm fs-6'
                    style={{height: '40px', background: '#0E9F6E', color: '#fff'}}
                    onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                      (e.currentTarget.style.backgroundColor = '#148363')
                    }
                    onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                      (e.currentTarget.style.backgroundColor = '#0E9F6E')
                    }
                    onClick={handleCheckedRemoveClick}
                    disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                  >
                    Remove Group
                  </button>
                </>
              ) : (
                <AddPersonnelGroupModal currentAuditContext={currentAuditContext} />
              )}
            </div>
          </div>
          <div className='card-body py-3 '>
            <div className='table-responsive ' style={{height: '55vh', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 '>
                <thead>
                  <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                    <th className='w-25px'>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          value='selectAll'
                          data-kt-check='true'
                          data-kt-check-target='.widget-9-check'
                          onChange={handleSelectAllChange}
                        />
                      </div>
                    </th>
                    <th className='min-w-350px'>GROUP</th>
                    <th className='min-w-100px'>NO. OF EMPLOYEES</th>
                    <th className='min-w-100px'>CREATED ON</th>
                    <th className='min-w-100px'>CREATED BY</th>
                    <th className='min-w-200px'>CHECKLIST</th>
                    <th className='min-w-50px'></th>
                  </tr>
                </thead>

                {filteredItems && filteredItems.length > 0 ? (
                  filteredItems.map((item) => (
                    <tbody>
                      <tr>
                        <td>
                          <div className='form-check form-check-sm form-check-custom form-check-solid'>
                            <input
                              className='form-check-input widget-9-check'
                              type='checkbox'
                              value={item.id}
                              checked={selectAll || checkedItems.includes(item.id)}
                              onChange={handleCheckChange}
                            />
                          </div>
                        </td>

                        <td>
                          <div className='d-flex align-items-center fw-bold'>
                            <div className='symbol symbol-20px me-15'>
                              <div className=' fs-7'>
                                {editingId === item.id ? (
                                  <>
                                    <div className='d-flex align-items-center'>
                                      <Form.Control
                                        type='text'
                                        id='kt_file_manager_rename_input'
                                        name='rename_folder_name'
                                        placeholder='Enter the new folder name'
                                        className='mw-250px me-3'
                                        style={{width: '100%', height: '40px'}}
                                        value={folderName}
                                        onChange={(e) => setFolderName(e.target.value)}
                                      />
                                      <Button
                                        className='btn btn-icon btn-sm btn-light-primary me-3'
                                        onClick={() => handleRename(item.id)}
                                      >
                                        <svg
                                          xmlns='http://www.w3.org/2000/svg'
                                          width='24'
                                          height='24'
                                          viewBox='0 0 24 24'
                                          fill='none'
                                          stroke='currentColor'
                                          stroke-width='2'
                                          stroke-linecap='round'
                                          stroke-linejoin='round'
                                          className='icon icon-tabler icons-tabler-outline icon-tabler-check mx-3'
                                        >
                                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                          <path d='M5 12l5 5l10 -10' />
                                        </svg>
                                      </Button>
                                      <Button
                                        className='btn btn-icon btn-sm btn-light-danger'
                                        onClick={handleCancel}
                                      >
                                        <svg
                                          xmlns='http://www.w3.org/2000/svg'
                                          width='24'
                                          height='24'
                                          viewBox='0 0 24 24'
                                          fill='none'
                                          stroke='currentColor'
                                          stroke-width='2'
                                          stroke-linecap='round'
                                          stroke-linejoin='round'
                                          className='icon icon-tabler icons-tabler-outline icon-tabler-x mx-3'
                                        >
                                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                          <path d='M18 6l-12 12' />
                                          <path d='M6 6l12 12' />
                                        </svg>
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  <div className='d-flex align-items-center fw-bold'>
                                    <div className='symbol symbol-20px me-15'>
                                      <div className='fs-6'>
                                        <span>{item.groupName}</span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex align-items-center justify-content-center fw-semibold'>
                            <div className='symbol symbol-20px me-15'>
                              <div className=' fs-6'>
                                <div>{groupCounts[item.groupName] || '-'}</div>
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className='d-flex align-items-center fw-semibold'>
                            <div className='symbol symbol-20px me-15'>
                              <div className=' fs-6'>
                                <div>{item.createdOn ? formatTimestamp(item.createdOn) : '--'}</div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex align-items-center fw-semibold'>
                            <div className='symbol symbol-20px me-15'>
                              <div className=' fs-6'>
                                <div>{item.createdBy}</div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <select
                            className='form-select form-select-sm'
                            data-control='select2'
                            data-placeholder='Select Checklist'
                            style={{width: '100%', height: '40px'}}
                            value={
                              checklists.some((c) => c.checklist_name === item.checklist)
                                ? item.checklist
                                : ''
                            }
                            onChange={(e) => handleChange(item.id, e)}
                            disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                          >
                            <option value='' disabled>
                              Select Checklist
                            </option>
                            {checklists
                              .filter((checklist) => checklist.tenantId === currentUser?.tenantId)
                              .map((checklist: any) => (
                                <option key={checklist.id} value={checklist.checklist_name}>
                                  {checklist.checklist_name}
                                </option>
                              ))}
                          </select>
                        </td>

                        <td>
                          <div className='dropdown mx-2'>
                            {currentUser?.role !== 'Auditor' && !currentAuditContext &&
                            <button
                              className='btn btn-secondary border-0 p-0 m-2'
                              type='button'
                              id='dropdownMenu2'
                              data-bs-toggle='dropdown'
                              aria-haspopup='true'
                              aria-expanded='false'
                            >
                              <img
                                src='/media/buttons/Options.svg'
                                alt='Options'
                                onClick={toggleDropdown}
                              />
                            </button>
                            }
                            <ul className='dropdown-menu' aria-labelledby='dropdownMenu2'>
                              <li>
                                <button
                                  className='dropdown-item text-hover-primary'
                                  type='button'
                                  style={{color: '#262626'}}
                                  onClick={() => handleEditClick(item.id, item.groupName)}
                                  disabled={currentUser?.role === 'Auditor'}
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='18'
                                    height='18'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    stroke-width='2'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-edit mx-2'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1' />
                                    <path d='M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z' />
                                    <path d='M16 5l3 3' />
                                  </svg>
                                  Rename Group
                                </button>
                              </li>

                              <li>
                                <button
                                  className='dropdown-item'
                                  type='button'
                                  style={{color: '#F15363'}}
                                  onClick={(e) => handleDeleteClick(item)}
                                  disabled={currentUser?.role === 'Auditor'}
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='18'
                                    height='18'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    stroke-width='2'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-trash mx-2 mb-1'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M4 7l16 0' />
                                    <path d='M10 11l0 6' />
                                    <path d='M14 11l0 6' />
                                    <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                    <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                                  </svg>{' '}
                                  Delete group
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  ))
                ) : (
                  <tbody>
                    <tr style={{height: '100%'}}>
                      {searchLength ? (
                        <td colSpan={6} className='text-center'>
                          <div className='m-20'>
                            <img src='/media/buttons/emptystate.svg' alt='' />
                            <div className='m-5 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No Groups Found</p>
                            </div>
                            <p className=''>
                              We couldn't find any personnel group that match your search. Maybe try
                              a different search!
                            </p>
                          </div>
                        </td>
                      ) : (
                        <td colSpan={6} className='text-center'>
                          {showEmptyState && (
                            <div className='m-20'>
                              <img src='/media/buttons/emptystate.svg' alt='' />
                              <div className='m-5 fs-5'>
                                <p style={{fontWeight: 'bold'}}>No Group Added</p>
                              </div>
                              <p>
                                All the personnel groups within your organization are required to
                                address here.
                              </p>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </div>
      </KTCard>
      <ToastContainer />
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        hideModal={() => {
          setIsDeleteModalOpen(false)
          setSelectedGroup(null)
        }}
        confirmBtnClickHandler={handleDeleteConfirm}
        toDeleteItemTitle={selectedGroup?.groupName}
        isLoading={isDeleting}
        title={`Delete ${selectedGroup?.groupName}?`}
        message={`Are you sure you want to delete the group '${selectedGroup?.groupName}'? This action cannot be undone.`}
        type='delete'
      />
      <ConfirmationModal
        isModalOpen={isBulkDeleteModalOpen}
        hideModal={() => setIsBulkDeleteModalOpen(false)}
        confirmBtnClickHandler={handleBulkDeleteConfirm}
        isLoading={isDeleting}
        title='Remove checked groups?'
        message={`Are you sure you want to remove ${checkedItems.length} selected groups? This action cannot be undone.`}
        confirmButtonText='Remove'
        type='delete'
      />
    </>
  )
}

export default PersonnelGroup
